import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ACCOUNTANT,
  CALLER,
  COMPANY_ADMIN,
  EDITED_SUCCESS_MSG,
  ERROR_MSG,
  REQUIRED_FIELD,
  SELECT_PRODUCT_CATEGORY_MSG,
  SELECT_PRODUCT_INTERESTED_MSG,
  SUPER_ADMIN,
} from "../../../../core/data/constants/constant";
import { useStages } from "../../../crmSetting/stages/hooks/useStages";
import { all_routes } from "../../../router/all_routes";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import { useDealData } from "../hooks/useDealData";
import DealApi from "../api/DealApi";
import { Deal } from "../models/DealModel";
import { useDispatch, useSelector } from "react-redux";
import EditDealPaymentDetails from "../components/EditDealPaymentDetails";
import EditDealVerificationRecord from "../components/EditDealVerificationRecord";
import { splitByUppercase } from "../../../../utils/helpers/helper";
import Select from "react-select";
import useProductOptions from "../../../../utils/hooks/useProductOptions";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import {
  getAllUrlParams,
  getNavigationUrl,
} from "../../../../utils/helpers/setAndGetUrlParams";
import { setHasNewFilters } from "../../../../core/data/redux/filterSlice";
import {
  updateDealsListCache,
  updateSingleDealCache,
} from "./updateDealsListCache";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { useCachedDealData } from "../hooks/getCachedDealData";
import queryClient from "../../../../config/queryClient";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";
import { lang } from "moment";

const EditDeal = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [forceReload, setForceReload] = useState(false);
  const reloading = useSelector((state) => state.moduleData.reloading);

  const cachedDealData = location?.state?.data;
  const { id: dealId } = useParams();
  const { dealData, isLoading } = useCachedDealData(
    dealId,
    cachedDealData,
    forceReload
  );

  const urlParams = getAllUrlParams();
  const verificationRecordId = location.state?.verificationRecordId;

  const { filters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];
  let { queryParams, params } = generateQueryParams(dealsFilters);

  const { user } = useSelector((state) => state?.auth);
  const userRole = user?.role?.name;
  const isEditableByAccountant =
    userRole === ACCOUNTANT ||
    userRole === SUPER_ADMIN ||
    userRole === COMPANY_ADMIN;
  const isEditableByCaller =
    userRole === CALLER ||
    userRole === SUPER_ADMIN ||
    userRole === COMPANY_ADMIN;

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();

  const hasZoomAccess = useFunctionalityAccess("zoomSetting");

  const dealDetailsUrl = `${route.dealsDetails.replace(
    ":id",
    dealData?._id
  )}?${queryString}`;

  const { stagesData } = useStages({
    ofCompany: dealData?.ofCompany?._id,
    module: MODULES.DEALS,
  });
  const stages = stagesData?.data?.data;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState({});
  const { productCategories, productNamesByCategory } = useProductOptions(
    dealData?.ofCompany?._id
  );

  useEffect(() => {
    if (!dealData?.editable && dealData?.currentStatus?.name) {
      toast.warning(
        ` Deal is converted to ${dealData?.currentStatus?.name} stage. Now it is not editable.`
      );
    }
  }, [dealData]);

  useEffect(() => {
    dispatch(setHasNewFilters());
  }, []);

  useEffect(() => {
    if (reloading) {
      setForceReload(true);
      queryClient.invalidateQueries(["deal", dealId]);
      dispatch(setReloading(false));
    }
  }, [reloading, setForceReload]);

  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);

  const mutationUpdate = useMutation({
    mutationFn: ({ id, dealData }) => DealApi.updateDeal(id, dealData),
    onSuccess: (res, { id, dealData }) => {
      if (res.status) {
        toast.success(EDITED_SUCCESS_MSG);
        navigate(dealDetailsUrl);

        updateSingleDealCache(id, res.data);

        updateDealsListCache({
          id,
          updatedData: res.data,
          page: urlParams?.page * 1,
          limit: urlParams?.limit * 1,
          queryString: params?.toString(),
          queryParams: queryParams?.toString(),
          companyId: urlParams?.companyid,
        });
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.msg || ERROR_MSG);
    },
  });

  const validationSchema = Yup.object({
    description: Yup.string(),
    probability: Yup.string(),
    productCategory: Yup.string(),
    alternateNumber: Yup.string().matches(
      /^(?:\d{10}|\+\d{1,4}\d{10})$/,
      "Mobile number must be either 10 digits or a valid international format (e.g., +914564566789)."
    ),
    productInterested: Yup.object()
      .shape({
        name: Yup.string().required(SELECT_PRODUCT_INTERESTED_MSG),
        id: Yup.number(),
        _id: Yup.string(),
      })
      .required("Product name is required"),
    textMessageAboutActivity: Yup.string(),
    currentStatus: Yup.object()
      .shape({
        name: Yup.string().required("Stage name is required."),
        id: Yup.number().required("Stage id is required."),
        color: Yup.string().required("Stage color is required."),
      })
      .required(REQUIRED_FIELD),
    verificationRecord: Yup.array().of(
      Yup.object().shape({
        paymentDate: Yup.string(),
        paymentId: Yup.string(),
        amountPaid: Yup.number(),
        salesOrderNumber: Yup.string(),
        oldPaymentRemarks: Yup.string(),
        paymentStatus: Yup.string(),
        remarkDate: Yup.string(),
        remark: Yup.string(),
      })
    ),
    paymentDetails: Yup.object().shape({
      amount: Yup.number(),
      totalPaymentVerified: Yup.string(),
      paymentReceived: Yup.string(),
      invoiceNumber: Yup.string(),
      sourceOfFund: Yup.string(),
      paymentStatusRemark: Yup.string(),
      paymentDescription: Yup.string(),
      loanAmount: Yup.number(),
      loanStatus: Yup.string(),
    }),
    city: Yup.string().required("City is required"),
    occupation: Yup.string(),
    language: Yup.string(),
  });

  const formik = useFormik({
    initialValues:
      dealData && dealData?._id
        ? {
            ...dealData,
            productCategory: dealData.productCategory || "",
            productInterested: dealData?.productInterested || {
              name: "",
              id: null,
              _id: "",
            },
            alternateNumber: dealData?.alternateNumber || "",
            city: dealData?.city || "",
            occupation: dealData?.occupation || "",
            language: dealData?.language || "",
          }
        : new Deal(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const updatedFields = {};
      const messages = [];

      // Compare values with original dealData
      for (const key in validationSchema.fields) {
        if (values[key] !== undefined && formik.dirty) {
          if (key === "currentStatus") {
            // Handle object comparison
            const oldValue = dealData[key]?.name || "N/A";
            const newValue = values[key]?.name || "N/A";

            if (oldValue !== newValue) {
              updatedFields[key] = values[key];
              messages.push(
                `<b>${MODULES.DEALS.slice(
                  0,
                  -1
                )} ${key}</b> updated from <b>${oldValue}</b> to <b>${newValue}</b>`
              );
            }
          } else if (key === "productInterested") {
            // Handle `productInterested` as an object change
            const oldProduct = dealData[key] || {
              name: "N/A",
              id: null,
              _id: "",
            };
            const newProduct = values[key] || {
              name: "N/A",
              id: null,
              _id: "",
            };

            const oldProductName = oldProduct.name || "N/A";
            const newProductName = newProduct.name || "N/A";

            if (oldProductName !== newProductName) {
              updatedFields[key] = values[key];
              messages.push(
                `<b>Product Interested</b> updated from <b>${oldProductName}</b> to <b>${newProductName}</b>`
              );
            }
          } else if (key === "paymentDetails") {
            // Track specific changes in paymentDetails fields
            const paymentUpdates = [];
            const oldPaymentDetails = dealData[key] || {};
            const newPaymentDetails = values[key] || {};

            // List of fields to track in paymentDetails
            const paymentFields = [
              "amount",
              "totalPaymentVerified",
              "paymentReceived",
              "invoiceNumber",
              "sourceOfFund",
              "paymentStatusRemark",
              "paymentDescription",
              "loanAmount",
              "loanStatus",
            ];

            paymentFields.forEach((field) => {
              const oldValue = oldPaymentDetails[field] ?? "N/A";
              const newValue = newPaymentDetails[field] ?? "N/A";

              if (oldValue !== newValue) {
                paymentUpdates.push(
                  `<b>Payment Details</b>: <b>${splitByUppercase(
                    field
                  )}</b> changed from <b>${oldValue}</b> to <b>${newValue}</b>`
                );
              }
            });

            if (paymentUpdates.length > 0) {
              updatedFields[key] = newPaymentDetails;
              messages.push(paymentUpdates.join(", "));
            }
          } else if (key === "verificationRecord") {
            // Track changes in verifiedRecords array
            const verifiedUpdates = [];
            const oldVerifiedRecords = dealData[key] || [];
            const newVerifiedRecords = values[key] || [];

            newVerifiedRecords.forEach((newRecord) => {
              // Match the old record using paymentId (or another unique identifier)
              const oldRecord =
                oldVerifiedRecords.find(
                  (rec) => rec.paymentId === newRecord.paymentId
                ) || {};

              const recordChanges = [];

              // List of fields to track in verifiedRecords
              const verifiedFields = [
                "paymentDate",
                "paymentId",
                "amountPaid",
                "salesOrderNumber",
                "oldPaymentRemarks",
                "paymentStatus",
                "remarkDate",
                "remark",
              ];

              verifiedFields.forEach((field) => {
                const oldValue =
                  typeof oldRecord[field] === "object"
                    ? JSON.stringify(oldRecord[field] ?? "N/A")
                    : oldRecord[field] ?? "N/A";

                const newValue =
                  typeof newRecord[field] === "object"
                    ? JSON.stringify(newRecord[field] ?? "N/A")
                    : newRecord[field] ?? "N/A";

                // Only add to changes if the value is different
                if (oldValue !== newValue) {
                  recordChanges.push(
                    `<b>Verified Record (Payment ID: ${
                      newRecord.paymentId || "N/A"
                    })</b>: <b>${splitByUppercase(
                      field
                    )}</b> changed from <b>${oldValue}</b> to <b>${newValue}</b>`
                  );
                }
              });

              // If there are changes, add to the updates
              if (recordChanges.length > 0) {
                verifiedUpdates.push(recordChanges.join(", "));
              }
            });

            // Only add to updatedFields and messages if there are actual changes
            if (verifiedUpdates.length > 0) {
              updatedFields[key] = newVerifiedRecords;
              messages.push(verifiedUpdates.join(", "));
            }
          } else {
            // Default case for other fields
            const oldValue = dealData[key] || "N/A";
            const newValue = values[key] || "N/A";

            if (oldValue !== newValue) {
              updatedFields[key] = newValue;
              messages.push(
                `<b>${key}</b> updated from <b>${oldValue}</b> to <b>${newValue}</b>`
              );
            }
          }
        }
      }

      if (values.paymentDetails?.totalPaymentVerified === "Approved") {
        updatedFields.dealVerifiedByAccountant = true;
        messages.push(`<b>Deal successfully verified by accountant.</b>`);
      } else {
        updatedFields.dealVerifiedByAccountant = false;
      }

      // If no updates, show a toast warning
      if (Object.keys(updatedFields).length === 0) {
        toast.warn("No changes were made to the deal data.");
        return;
      }

      // Log or save timeline message excluding unchanged fields
      const timelineMessage = messages.length > 0 ? messages.join(", ") : "";

      // Include the timelineMessage only if there are other field updates
      mutationUpdate.mutate({
        id: dealId,
        dealData: {
          ...updatedFields,
          ...(timelineMessage
            ? { textMessageAboutActivity: timelineMessage }
            : {}),
        },
      });
    },
  });

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      Object.values(formik.errors).forEach((error) => {
        if (typeof error === "object") {
          Object.values(error).forEach((nestedError) => {
            toast.error(nestedError);
          });
        } else {
          toast.error(error);
        }
      });
    }
  }, [formik.submitCount]);

  useEffect(() => {
    if (dealData?.data) {
      formik.setValues({
        ...formik.initialValues,
        ...dealData.data,
      });
    }
  }, [dealData]);

  const statusChangeHandler = (e) => {
    const selectedOption = JSON.parse(e.target.value);
    setSelectedStage(selectedOption);
    formik.setFieldValue("currentStatus", selectedOption);
    if (selectedOption?.toDeal) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <div className="page-wrapper">
            <div className="content">
              <div className="row">
                <div className="col-md-12">
                  {/* Page Header */}
                  <PageHeader pageName="Edit Deal" moduleName="deal" />
                  {/* /Page Header */}
                </div>
              </div>
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <div
                          onClick={() =>
                            navigate(getNavigationUrl(route.deals))
                          }
                          className="navigate-link"
                        >
                          {" "}
                          <i className="ti ti-arrow-narrow-left text-capitalize me-1" />{" "}
                          Deals
                        </div>
                      </li>
                      <li className="text-capitalize">
                        <div
                          onClick={() => navigate(dealDetailsUrl)}
                          className="navigate-link"
                        >
                          {dealData?.firstName || "N/A"}{" "}
                          {dealData?.lastName || "N/A"}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <form onSubmit={formik?.handleSubmit}>
                  <div className="accordion" id="main_accordion">
                    {/* Basic Info */}
                    <div className="accordion-item rounded mb-3">
                      <div
                        className={`accordion-header ${
                          formik.errors.city && formik.touched.city
                            ? "bg-danger"
                            : ""
                        }`}
                      >
                        <Link
                          to="#"
                          className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                          data-bs-toggle="collapse"
                          data-bs-target="#basic"
                        >
                          <span className="avatar avatar-md rounded text-dark border me-2">
                            <i className="ti ti-user-plus fs-20" />
                          </span>
                          Basic Info
                        </Link>
                      </div>
                      <div
                        className="accordion-collapse collapse show"
                        id="basic"
                        data-bs-parent="#main_accordion"
                      >
                        <div className="accordion-body border-top">
                          <div className="row">
                            {/* First Name */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  First Name{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control text-capitalize"
                                    name="firstName "
                                    value={dealData?.firstName}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Last Name */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Last Name{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control text-capitalize"
                                    name="lastName"
                                    value={dealData?.lastName}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Email */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Email <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={dealData?.email}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Phone */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">Phone</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="mobile"
                                    value={dealData?.mobile}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* alternateNumber */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Alternate Phone Number
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="alternateNumber"
                                    className="form-control"
                                    value={
                                      formik.values.alternateNumber ||
                                      dealData?.alternateNumber
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched?.alternateNumber &&
                                formik.errors?.alternateNumber ? (
                                  <div className="text-danger">
                                    {formik.errors?.alternateNumber}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* probability */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Probability
                                </label>
                                <div className="input-group">
                                  <select
                                    name="probability"
                                    className="form-select"
                                    value={
                                      formik.values.probability ||
                                      dealData?.probability
                                    }
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        "probability",
                                        e.target.value
                                      );
                                    }}
                                  >
                                    <option value="">Select Probability</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                  </select>
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Company List */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Company Name
                                </label>
                                <div className="input-group">
                                  <input
                                    className="form-control"
                                    name="ofCompany"
                                    value={dealData?.ofCompany?.name || ""}
                                    disabled
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Product Category Select Box */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Product Category
                                </label>
                                <Select
                                  className="select2"
                                  classNamePrefix="react-select"
                                  options={productCategories} // Options for product categories
                                  placeholder="Choose a product category"
                                  name="productCategory"
                                  value={
                                    productCategories?.find(
                                      (option) =>
                                        option.label ===
                                        formik.values.productCategory
                                    ) || null
                                  }
                                  onChange={(selectedOption) => {
                                    formik.setFieldValue(
                                      "productCategory",
                                      selectedOption?.label
                                    );
                                    formik.setFieldValue(
                                      "productInterested",
                                      null
                                    ); // Reset productInterested on category change
                                  }}
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      "productCategory",
                                      true
                                    )
                                  }
                                />
                                {formik.touched.productCategory &&
                                formik.errors.productCategory ? (
                                  <div className="text-danger">
                                    {formik.errors.productCategory &&
                                      SELECT_PRODUCT_CATEGORY_MSG}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Product Name Select Box */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Product Name
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  className="select2"
                                  classNamePrefix="react-select"
                                  options={
                                    (productNamesByCategory &&
                                    formik?.values?.productCategory
                                      ? productNamesByCategory[
                                          formik.values.productCategory
                                        ]
                                      : []) || []
                                  }
                                  placeholder="Choose a product name"
                                  name="productInterested"
                                  value={
                                    formik.values.productInterested || null
                                  } // Pass the full object to Select
                                  onChange={(selectedOption) =>
                                    formik.setFieldValue(
                                      "productInterested",
                                      selectedOption
                                    )
                                  } // Save the full object on selection
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      "productInterested",
                                      true
                                    )
                                  }
                                  getOptionLabel={(option) => option.name} // Show product name in dropdown
                                  getOptionValue={(option) => option._id} // Ensure unique value is used internally
                                />

                                {/* Show validation error */}
                                {formik.touched.productInterested &&
                                formik.errors.productInterested ? (
                                  <div className="text-danger">
                                    {formik.errors.productInterested.name}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* current status */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">Status</label>
                                <div className="input-group">
                                  <select
                                    name="currentStatus"
                                    className="form-select text-capitalize"
                                    value={
                                      JSON.stringify(
                                        formik.values.currentStatus
                                      ) || ""
                                    }
                                    onChange={(e) => statusChangeHandler(e)}
                                    onBlur={() =>
                                      formik.setFieldTouched(
                                        "currentStatus",
                                        true
                                      )
                                    }
                                  >
                                    <option value="">Select a stage</option>
                                    {stages?.map((stage) => (
                                      <option
                                        className="text-capitalize"
                                        key={stage?.id}
                                        value={JSON.stringify(stage)} // Pass the entire stage object
                                        // style={{
                                        //   backgroundColor: `${stage?.color}`,
                                        // }}
                                      >
                                        {stage?.name}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>

                                {formik.touched.currentStatus &&
                                formik.errors.currentStatus ? (
                                  <div className="text-danger">
                                    {formik.errors.currentStatus}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* city */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  City<span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    name="city"
                                    className="form-control text-capitalize"
                                    value={formik.values.city || dealData?.city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                                {formik.touched?.city && formik.errors?.city ? (
                                  <div className="text-danger">
                                    {formik.errors?.city}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/*Zoom Meeting Status */}
                            {hasZoomAccess && (
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="col-form-label">
                                    Zoom Meeting Status
                                  </label>
                                  <div className="input-group">
                                    <input
                                      className="form-control text-capitalize pt-0 fs-6"
                                      value={formik.values?.meetingStatus
                                        .split("-")
                                        .join(" ")}
                                    />
                                    <div className="input-group-text">
                                      <i className="ti ti-lock"></i>
                                    </div>
                                  </div>

                                  {formik.touched.meetingStatus &&
                                  formik.errors.meetingStatus ? (
                                    <div className="text-danger">
                                      {formik.errors.meetingStatus}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            )}

                            {/* Preferred Language */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Preferred Language
                                </label>
                                <select
                                  name="language"
                                  className="form-select"
                                  value={formik.values.language}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                >
                                  <option value="">Select Language</option>
                                  <option value="Marathi">Marathi</option>
                                  <option value="Hindi">Hindi</option>
                                  <option value="Engilsh">English</option>
                                </select>
                                {formik.touched.language &&
                                formik.errors.language ? (
                                  <div className="text-danger">
                                    {formik.errors.language}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* Occupation */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="col-form-label">
                                  Occupation
                                </label>
                                <div className="input-group">
                                  <input
                                    className="form-control text-capitalize pt-0 fs-6"
                                    value={
                                      formik.values?.occupation
                                        ?.split("-")
                                        .join(" ") || "N/A"
                                    }
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-lock"></i>
                                  </div>
                                </div>

                                {formik.touched.occupation &&
                                formik.errors.occupation ? (
                                  <div className="text-danger">
                                    {formik.errors.occupation}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {/* description */}
                            <div className="col-md-12">
                              <div className="mb-0">
                                <label className="col-form-label">
                                  Description
                                </label>
                                <div className="input-group">
                                  <textarea
                                    className="form-control"
                                    name="description"
                                    rows={5}
                                    value={formik.values.description || ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <div className="input-group-text">
                                    <i className="ti ti-pencil" />
                                  </div>
                                </div>
                              </div>
                              {formik.touched.description &&
                              formik.errors.description ? (
                                <div className="text-danger">
                                  {formik.errors.description}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account / payment details */}
                    <div className="accordion-item rounded mb-3">
                      <div className="accordion-header">
                        <Link
                          to="#"
                          className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                          data-bs-toggle="collapse"
                          data-bs-target="#payment"
                        >
                          <span className="avatar avatar-md rounded text-dark border me-2">
                            <i className="ti ti-user-plus fs-20" />
                          </span>
                          Account / Payment Details
                        </Link>
                      </div>
                      <div
                        className="accordion-collapse collapse show"
                        id="payment"
                        data-bs-parent="#main_accordion"
                      >
                        <div className="accordion-body border-top">
                          <div className="row">
                            <EditDealVerificationRecord
                              formik={formik}
                              userRole={userRole}
                              isEditableByAccountant={isEditableByAccountant}
                              isEditableByCaller={isEditableByCaller}
                              verificationRecordId={verificationRecordId}
                              dealData={dealData}
                            />
                            <EditDealPaymentDetails
                              formik={formik}
                              userRole={userRole}
                              isEditableByAccountant={isEditableByAccountant}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={
                        mutationUpdate?.isPending || !dealData?.editable
                      }
                    >
                      {mutationUpdate?.isPending ? (
                        "Updating..."
                      ) : !dealData?.editable ? (
                        <>
                          <span className="me-2">Update</span>{" "}
                          <i className="ti ti-lock fs-5" />
                        </>
                      ) : (
                        "Update"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Confirmation Modal */}
          {showConfirmModal && (
            <div className="modal fade show d-block" role="dialog">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Stage Conversion</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowConfirmModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body ">
                    <p>
                      Are you sure you want to move this lead to the{" "}
                      <strong className="text-capitalize">
                        {selectedStage?.name}
                      </strong>{" "}
                      stage? This action will convert the lead into a deal, and
                      it cannot be undone.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-light"
                      onClick={() => setShowConfirmModal(false)}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleConfirm}>
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default EditDeal;
